/** UI copy for the Inbox (Notifications, Chat, Queries). Shared labels (Cancel, Send…) live in `COMMON_TEXT`. */
export const INBOX_TEXT = {
  common: {
    admin: 'Admin',
    raisedBy: 'Raised by',
    to: 'to',
    markAsRead: 'Mark as read',
    queryNoLongerExists: 'That query no longer exists',
    answerPlaceholder: 'What do you need answered?',
    openQueries: 'Open queries',
    resolvedQueries: 'Resolved queries',
  },
  page: {
    title: 'Inbox',
    exitFullScreen: 'Exit full screen (Esc)',
    fullScreen: 'Full screen',
  },
  tabs: {
    notifications: 'Notification',
    chat: 'Chat',
    queries: 'Queries',
  },
  notifications: {
    searchPlaceholder: 'Search client name or code',
    tiles: {
      queries: 'Queries Notifications',
      workflow: 'Workflow Notifications',
      chat: 'Chat Notifications',
      billing: 'Billing Notifications',
      payments: 'Payment Reminders',
    },
    readOptions: {
      all: 'All',
      unread: 'Unread',
      read: 'Read',
      archived: 'Archived',
    },
    periodOptions: {
      all: 'All time',
      today: 'Today',
      week: 'This week',
      month: 'This month',
      custom: 'Custom range',
    },
    taskNoLongerExists: 'That task no longer exists',
    popup: {
      titles: {
        queries: 'Query notification',
        workflow: 'Workflow notification',
        chat: 'Chat message',
        billing: 'Billing notification',
        payments: 'Payment reminder',
        system: 'System notification',
      },
      type: 'Type',
      user: 'User',
      email: 'Email',
      request: 'Request',
      client: 'Client',
      task: 'Task',
      query: 'Query',
      step: 'Current step',
      status: 'Status',
      from: 'From',
      message: 'Message',
      invoice: 'Invoice #',
      jurisdiction: 'Jurisdiction',
      details: 'Details',
      received: 'Date & time',
      openDashboard: 'Open in Dashboard',
      openQuery: 'Open query',
      openBilling: 'Open in Billing',
      openEstimates: 'Open Estimated Tax',
      openUsers: 'Open User Management',
      openChat: 'Open chat',
    },
    markUnread: 'Mark unread',
    archive: 'Archive',
    unarchive: 'Unarchive',
    actions: 'Actions',
    columns: {
      type: 'Type',
      dateTime: 'Date & Time',
      clientName: 'Client Name',
      invoiceNo: 'Invoice #',
      jurisdiction: 'Jurisdiction',
      details: 'Details',
      currentStep: 'Current Step',
      allocatedBy: 'Allocated By',
      currentStatus: 'Current Status', // workflow
    },
    typeQueries: 'Queries',
    typeWorkflow: 'Workflow',
    typeSystem: 'System',
    emptyUnread: 'Nothing unread.',
    emptyRead: 'Nothing read yet.',
    emptyArchived: 'Nothing archived.',
    emptyBilling: 'No billing notifications yet.',
    emptyPayments:
      "No payment reminders yet — turn on Send Reminder in a client's Estimated Tax ACH Payment Details.",
    emailClient: 'Email client',
    noClientEmail: 'This client has no email address on file — add one in Client Master',
    reminderSubject: (jurisdiction: string, client: string) =>
      `Estimated Tax Payment Reminder — ${jurisdiction} — ${client}`,
    reminderBody: (client: string, detail: string) =>
      `Dear ${client},\n\n${detail}. Please let us know once the payment has been made.\n\nThank you.`,
    empty: 'No notifications yet.',
    notificationTypes: 'Notification types',
    readState: 'Read state',
    rangeTo: 'to',
    markAllRead: 'Mark all read',
    chatUnread: {
      empty: 'No unread chat messages.',
      label: 'Unread chats',
      lastMessage: 'Last message',
      received: 'Received',
      openChat: 'Open chat',
    },
  },
  chat: {
    searchChats: 'Search chats',
    savedMessages: 'Saved messages',
    conversations: 'Conversations',
    noMatches: 'No chats match your search.',
    noStaff: 'No other staff to chat with.',
    selectConversation: 'Select a conversation to start chatting.',
    online: 'Online',
    offline: 'Offline',
    lastSeen: (when: string) => `Last seen ${when}`,
    unpinChat: 'Unpin chat',
    pinChat: 'Pin chat',
    markAsUnread: 'Mark as unread',
    deleteChat: 'Delete chat',
    deleteConfirm: (name: string) => `Delete this conversation with ${name}? This can't be undone.`,
    chatWith: (name: string) => `Chat with ${name}`,
    pinned: 'Pinned',
    unread: 'Unread',
    chatOptions: 'Chat options',
    unavailable: "That conversation isn't available.",
    emptyMessageToast: 'Enter a message or attach a file',
    closeConversation: 'Close conversation',
    messages: 'Messages',
    noMessagesSayHello: 'No messages yet — say hello.',
    noMessagesYet: 'No messages yet',
    removeFromSaved: 'Remove from saved',
    saveMessage: 'Save message',
    editMessage: 'Edit message',
    messageActions: 'Message actions',
    saved: 'Saved',
    editMessageText: 'Edited message text',
    edited: 'Edited',
    attachFiles: 'Attach files',
    savedCount: (n: number) => `${n} saved message${n === 1 ? '' : 's'}`,
    closeSavedMessages: 'Close saved messages',
    noSavedMessages:
      'No saved messages yet — hover any message in a conversation and tap the star to save it here.',
    youPrefix: 'You: ',
    sentAttachment: 'Sent an attachment',
    today: 'Today',
    yesterday: 'Yesterday',
  },
  queries: {
    allServices: 'All services',
    raisedByUser: (name: string) => `Raised by ${name}`,
    raisedByAnyone: 'Raised by anyone',
    allClients: 'All clients',
    breadcrumb: 'Breadcrumb',
    newQuery: '+ New query',
    clientsWithQueries: 'Clients with queries',
    noTicketsMatch: 'No tickets match these filters.',
    services: 'Services',
    statusTiles: {
      label: 'Query status',
      all: 'All queries',
    },
    hasOpenQueries: 'Has open queries',
    allResolved: 'All resolved',
    noQueriesMatch: 'No queries match this filter.',
    noQueriesYet: 'No queries yet.',
    open: 'Open',
    resolved: 'Resolved',
    listLabel: 'Queries',
    thread: {
      label: 'Query thread',
      close: 'Close thread',
      resolvedBy: 'resolved by',
      editQuery: 'Edit query',
      deleteQuery: 'Delete query',
      queryPrefix: 'Query:',
      queryText: 'Query text',
      replies: 'Replies',
      emptyRemarkToast: 'Enter a remark or attach a file',
      emptyTextToast: "Query text can't be empty",
      markResolved: 'Mark resolved',
      reopen: 'Reopen',
      actorsSeparator: ' or ',
      onlyActors: (actors: string, isOpen: boolean) =>
        `Only ${actors}, or an admin, can ${isOpen ? 'respond' : 'reopen this'}.`,
    },
    deleteDialog: {
      header: 'Delete this query',
      submit: 'Delete query',
      removes: 'This removes the query and its whole reply thread.',
      repliesDeleted: (n: number) => `${n} ${n === 1 ? 'reply' : 'replies'} will be deleted along with it. `,
    },
    raiseDialog: {
      header: 'Raise a new query',
      submit: 'Raise query',
      chooseClientFirstToast: 'Search and choose a client first',
      chooseServiceToast: 'Choose which service this query is about',
      chooseRecipientsToast: 'Choose who to point this query to',
      enterRemarksToast: 'Enter the remarks or description',
      clientPlaceholder: 'Search by name, code or entity type',
      chooseService: '— Choose a service —',
      chooseClientFirst: '— Choose a client first —',
      remarks: 'Remarks / description',
    },
  },
  recipients: {
    label: 'Point this query to',
    directlyToClient: 'Directly To Client',
    choosePeople: 'Choose people',
  },
  taskQueries: {
    incompleteRowToast: "Finish or remove the query row that's missing recipients or text",
    emptyToast: 'Add at least one query with recipients and text',
    resolvedBy: (name: string, when: string) => ` · resolved by ${name} ${when}`,
    repliesSuffix: (n: number) => ` · ${n} ${n === 1 ? 'reply' : 'replies'}`,
    openOnServiceHolds: (n: number, service: string) =>
      `${n} open ${n === 1 ? 'query' : 'queries'} on ${service} for this client — this return can't move past review until ${n === 1 ? 'it is' : 'they are'} resolved.`,
    openOnService: (n: number, service: string) =>
      `${n} open ${n === 1 ? 'query' : 'queries'} on ${service} for this client.`,
    noQueries: (service: string) => `No queries on ${service} for this client.`,
    raiseAnother: 'Raise another query',
    raiseOne: 'Raise a query',
    queryLabel: 'Query',
    raiseSubmit: 'Raise 1 query',
  },
} as const;
